<template>
  <div class="express-companies">
    <h2>支持的物流公司</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>物流公司列表</span>
          <div class="button-group">
            <el-button type="primary" @click="handleSync" :loading="syncLoading">
              <el-icon><Refresh /></el-icon>
              同步物流公司
            </el-button>
            <el-button type="info" @click="handleViewConfigured" :loading="viewConfiguredLoading">
              <el-icon><View /></el-icon>
              查询同步月结账户
            </el-button>
          </div>
        </div>
        <div class="sync-tip">
          <el-alert
            title="同步提示"
            type="warning"
            description="点击查询同步月结账户，系统会自动更新管理系统的月结账号状态和对应账号的快递客户编码"
            show-icon
            :closable="false"
          />
        </div>
      </template>
      
      <!-- 已配置物流账户弹窗 -->
      <el-dialog
        v-model="configuredAccountsDialogVisible"
        title="已配置物流账户"
        width="60%"
        :close-on-click-modal="false"
      >
        <el-table
          v-loading="configuredAccountsLoading"
          :data="configuredAccountsList"
          stripe
          border
          style="width: 100%"
        >
          <el-table-column prop="delivery_id" label="物流编码" />
          <el-table-column prop="delivery_name" label="物流公司名称">
            <template #default="scope">
              <div class="company-name-container">
                <span>{{ scope.row.delivery_name }}</span>
                <span v-if="scope.row.has_monthly_settlement" class="monthly-badge">月</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="biz_id" label="快递客户编码" />
          <el-table-column prop="remark" label="备注" />
        </el-table>
      </el-dialog>
      
      <!-- 备注信息 -->
      <div class="remark-info">
        <el-alert
          title="物流配置说明"
          type="info"
          description="已配置月结账户的物流公司将优先使用月结方式，未配置月结的则使用散单现结方式。请根据实际业务需求启用或关闭对应物流公司。"
          show-icon
          :closable="false"
        />
      </div>
      
      <!-- 物流公司列表 -->
      <div class="companies-table-container">
        <el-table
          v-loading="loading"
          :data="companiesList"
          stripe
          border
          style="width: 100%"
        >
          <el-table-column prop="delivery_id" label="物流编码" />
          <el-table-column prop="delivery_name" label="物流公司名称" />
          <el-table-column prop="has_monthly_settlement" label="是否已配置月结">
            <template #default="scope">
              <el-tag :type="scope.row.has_monthly_settlement ? 'success' : 'info'">
                {{ scope.row.has_monthly_settlement ? '是' : '否' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="biz_id" label="快递客户编码" />
          <el-table-column prop="enabled" label="启用状态">
            <template #default="scope">
              <el-switch
                v-model="scope.row.enabled"
                @change="handleStatusChange(scope.row)"
                :active-value="true"
                :inactive-value="false"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import * as expressCompanyApi from '../../api/expressCompany';
import { Refresh, View } from '@element-plus/icons-vue';

// 加载状态
const loading = ref(false);
const syncLoading = ref(false);
const viewConfiguredLoading = ref(false);
const configuredAccountsLoading = ref(false);

// 物流公司列表
const companiesList = ref([]);

// 已配置账户相关
const configuredAccountsDialogVisible = ref(false);
const configuredAccountsList = ref([]);

// 加载物流公司列表
const loadCompanies = async () => {
  loading.value = true;
  try {
    const response = await expressCompanyApi.fetchExpressCompanies();
    if (response.code === 200) {
      // 按启用状态倒序排序，启用的放前面
      companiesList.value = response.data.sort((a, b) => {
        return b.enabled - a.enabled;
      });
    } else {
      ElMessage.error('获取物流公司列表失败');
    }
  } catch (error) {
    ElMessage.error('获取物流公司列表失败');
    console.error(error);
  } finally {
    loading.value = false;
  }
};

// 同步物流公司
const handleSync = async () => {
  syncLoading.value = true;
  try {
    const response = await expressCompanyApi.syncExpressCompaniesFromWechat();
    if (response.code === 200) {
      ElMessage.success('同步物流公司成功');
      // 重新加载并排序列表
      loadCompanies();
    } else {
      ElMessage.error('同步物流公司失败');
    }
  } catch (error) {
    ElMessage.error('同步物流公司失败');
    console.error(error);
  } finally {
    syncLoading.value = false;
  }
};

// 处理状态变更
const handleStatusChange = async (company) => {
  try {
    const response = await expressCompanyApi.updateExpressCompanyStatus(company.id);
    if (response.code === 200) {
      ElMessage.success('状态更新成功');
      // 重新加载并排序列表
      loadCompanies();
    } else {
      // 恢复原来的状态
      company.enabled = !company.enabled;
      ElMessage.error('状态更新失败');
    }
  } catch (error) {
    // 恢复原来的状态
    company.enabled = !company.enabled;
    ElMessage.error('状态更新失败');
    console.error(error);
  }
};

// 查看已配置物流账户
const handleViewConfigured = async () => {
  viewConfiguredLoading.value = true;
  try {
    const response = await expressCompanyApi.getConfiguredExpressAccounts();
    if (response.code === 200) {
      configuredAccountsList.value = response.data;
      configuredAccountsDialogVisible.value = true;
    } else {
      ElMessage.error('获取已配置物流账户失败');
    }
  } catch (error) {
    ElMessage.error('获取已配置物流账户失败');
    console.error(error);
  } finally {
    viewConfiguredLoading.value = false;
  }
};

// 组件挂载时加载数据
onMounted(() => {
  loadCompanies();
});
</script>

<style scoped>
.express-companies {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button-group {
  display: flex;
  gap: 10px;
}

.remark-info {
  margin-bottom: 20px;
}

.companies-table-container {
  margin-top: 0;
}

/* 月字角标样式 */
.company-name-container {
  position: relative;
  display: inline-block;
  padding-right: 16px;
}

.monthly-badge {
  position: absolute;
  top: 2px;
  right: 0;
  background-color: #faad14;
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 10px;
  line-height: 1;
  font-weight: bold;
}

.sync-tip {
  margin-top: 10px;
}
</style>
